import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useWebSocket } from "../hooks/useWebSocket";
import GamePhase from "../components/GamePhase";
import PlayerList from "../components/PlayerList";
import { PencilIcon } from "../components/PencilIcon";
import { EyeIcon } from "../components/EyeIcon";
import "./GameRoom.css";

const ROLE_NAMES = {
  villager: "Dân Làng",
  werewolf: "Ma Sói",
  seer: "Tiên Tri",
  doctor: "Bảo Vệ",
  witch: "Phù Thuỷ",
  hunter: "Thợ săn",
  lycan: "Bán sói",
};

// Generate a simple UUID
function generateUUID() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Get stored player info from localStorage
function getStoredPlayerInfo() {
  try {
    const storedId = localStorage.getItem("werewolf_playerId");
    const storedName = localStorage.getItem("werewolf_playerName");
    if (storedId && storedName) {
      return { playerId: storedId, playerName: storedName };
    }
  } catch (error) {
    console.error("Error reading from localStorage:", error);
  }
  return null;
}

// Store player info in localStorage
function storePlayerInfo(playerId, playerName) {
  try {
    localStorage.setItem("werewolf_playerId", playerId);
    localStorage.setItem("werewolf_playerName", playerName);
  } catch (error) {
    console.error("Error writing to localStorage:", error);
  }
}

function GameRoom() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [playerName, setPlayerName] = useState("");
  const [playerId, setPlayerId] = useState(null);
  const [gameState, setGameState] = useState(null);
  const [isFirstTime, setIsFirstTime] = useState(false);
  const [showConnectionError, setShowConnectionError] = useState(false);
  const [deathOverlayDismissed, setDeathOverlayDismissed] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState("");
  const [voteTieMessage, setVoteTieMessage] = useState(null); // Store voteTie message for display
  const [werewolfChatMessages, setWerewolfChatMessages] = useState([]); // Store werewolf chat messages
  const [showRoles, setShowRoles] = useState(true); // Toggle for showing/hiding roles

  // Get current player's name from gameState
  const currentPlayerName =
    gameState && gameState.myId
      ? gameState.players.find((p) => p.id === gameState.myId)?.name
      : playerName;

  // Check if current player is dead
  const isDead =
    gameState &&
    gameState.myId &&
    gameState.players.find((p) => p.id === gameState.myId)?.isAlive === false;

  // Reset death overlay dismissal when player becomes alive again (new game)
  useEffect(() => {
    if (gameState && gameState.myId) {
      const player = gameState.players.find((p) => p.id === gameState.myId);
      if (player && player.isAlive) {
        setDeathOverlayDismissed(false);
      }
    }
  }, [gameState]);

  // Reset death overlay dismissal when player becomes alive again (new game)
  useEffect(() => {
    if (gameState && gameState.myId) {
      const player = gameState.players.find((p) => p.id === gameState.myId);
      if (player && player.isAlive) {
        setDeathOverlayDismissed(false);
      }
    }
  }, [gameState]);

  // Clear voteTieMessage when phase changes (unless it's for the current phase)
  useEffect(() => {
    if (gameState && voteTieMessage) {
      if (voteTieMessage.phase !== gameState.phase) {
        setVoteTieMessage(null);
      }
    }
  }, [gameState, gameState?.phase, voteTieMessage]);

  // Clear werewolf chat messages when phase changes away from werewolf phase
  useEffect(() => {
    if (gameState?.phase && gameState.phase !== 'werewolf') {
      setWerewolfChatMessages([]);
    }
  }, [gameState?.phase]);

  // Reset showRoles when game finishes (auto-show roles)
  useEffect(() => {
    if (gameState?.gameState === "finished") {
      setShowRoles(true);
    }
  }, [gameState?.gameState]);

  // Check localStorage on mount
  useEffect(() => {
    const stored = getStoredPlayerInfo();
    if (stored) {
      setPlayerId(stored.playerId);
      setPlayerName(stored.playerName);
    } else {
      setIsFirstTime(true);
    }
  }, []);

  const handleMessage = (data) => {
    if (data.type === "connected") {
      console.log("Connection confirmed by server");
      // Connection is established, do nothing special
    } else if (data.type === "joined") {
      setPlayerId(data.playerId);
      setGameState(data.gameState);
      // Sync werewolf chat messages from gameState if available
      if (data.gameState && data.gameState.werewolfChatMessages) {
        setWerewolfChatMessages(data.gameState.werewolfChatMessages);
      }
    } else if (data.type === "gameState") {
      console.log(
        "Received gameState update:",
        data.gameState?.gameState,
        "phase:",
        data.gameState?.phase,
        "players:",
        data.gameState?.players?.length
      );
      // Always update state - create new object reference to ensure React detects change
      if (data.gameState) {
        // Deep clone to ensure React detects all changes
        const newState = JSON.parse(JSON.stringify(data.gameState));
        console.log(
          "Updating gameState - new phase:",
          newState.phase,
          "old phase:",
          gameState?.phase
        );
        setGameState(newState);
        // Sync werewolf chat messages from gameState if available
        // But preserve any optimistic messages (messages sent but not yet confirmed by server)
        if (newState && newState.werewolfChatMessages) {
          setWerewolfChatMessages((prev) => {
            // Keep optimistic messages that haven't been confirmed yet
            const optimisticMessages = prev.filter(msg => msg.isOptimistic);
            // Merge server messages with optimistic messages
            // Remove optimistic messages that have been confirmed by server
            const serverMessages = newState.werewolfChatMessages || [];
            const confirmedOptimistic = optimisticMessages.filter(optMsg => 
              serverMessages.some(serverMsg => 
                serverMsg.senderName === optMsg.senderName &&
                serverMsg.text === optMsg.text &&
                Math.abs((serverMsg.timestamp || 0) - optMsg.timestamp) < 5000
              )
            );
            // Keep only unconfirmed optimistic messages
            const unconfirmedOptimistic = optimisticMessages.filter(optMsg => 
              !confirmedOptimistic.some(confirmed => 
                confirmed.senderName === optMsg.senderName &&
                confirmed.text === optMsg.text &&
                confirmed.timestamp === optMsg.timestamp
              )
            );
            // Combine server messages with unconfirmed optimistic messages
            return [...serverMessages, ...unconfirmedOptimistic];
          });
        }
        // Clear loading when state updates (this will trigger GamePhase to clear its loading state via phase change)
      }
    } else if (data.type === "stateUpdate") {
      // Server requested state update - refresh immediately
      console.log("State update requested by server");
      if (playerId && roomId) {
        send({ type: "getState", roomId, playerId });
      } else if (roomId) {
        // Even without playerId, try to get state (might be waiting to join)
        send({ type: "getState", roomId });
      }
    } else if (data.type === "playerJoined") {
      // Refresh state immediately when new player joins
      console.log(
        "New player joined:",
        data.playerName,
        "playerId:",
        data.playerId
      );
      // Always request state update, even if we don't have playerId yet
      if (roomId) {
        if (playerId) {
          send({ type: "getState", roomId, playerId });
        } else {
          // Request state without playerId to get updated player list
          send({ type: "getState", roomId });
        }
      }
    } else if (data.type === "gameStarted") {
      // Refresh state immediately when game starts
      console.log("Game started! Requesting updated state...");
      // Request state with a small delay to ensure server has finished initializing
      setTimeout(() => {
        if (playerId && roomId) {
          console.log("Requesting game state after game start");
          send({ type: "getState", roomId, playerId });
        } else if (roomId) {
          // Even without playerId, try to get state
          console.log("Requesting game state (no playerId) after game start");
          send({ type: "getState", roomId });
        }
      }, 100);
    } else if (data.type === "gameFinished") {
      setGameState((prev) => ({
        ...prev,
        gameState: "finished",
        winner: data.winner,
      }));
    } else if (data.type === "gameReset") {
      // Game was reset, refresh state immediately
      console.log("Game reset detected, refreshing state...");
      // Don't clear gameState to null - just update it when new state arrives
      // This ensures polling continues to work
      // Request fresh state immediately
      setTimeout(() => {
        if (playerId && roomId) {
          console.log("[GameRoom] Requesting state after game reset...");
          send({ type: "getState", roomId, playerId });
        } else if (roomId) {
          console.log("[GameRoom] Requesting state after game reset (no playerId)...");
          send({ type: "getState", roomId });
        }
      }, 100);
    } else if (data.type === "actionTaken") {
      // Action was taken by another player
      console.log(
        "Action taken by player:",
        data.playerId,
        "action:",
        data.action,
        "phaseAdvanced:",
        data.phaseAdvanced
      );
      // If phase advanced, state should already be received via broadcastGameState
      // But request state update as backup to ensure we have latest state
      if (playerId && roomId) {
        // Request immediately - no delay needed
        send({ type: "getState", roomId, playerId });
      }
    } else if (data.type === "phaseChanged") {
      // Phase changed, refresh state immediately
      console.log(
        "Phase changed:",
        data.phase,
        "gameState:",
        data.gameState,
        "oldPhase:",
        data.oldPhase
      );
      if (playerId && roomId) {
        // Request state immediately - broadcastGameState should have sent it, but request as backup
        send({ type: "getState", roomId, playerId });
      }
    } else if (data.type === "renamed") {
      // Player name was successfully renamed
      console.log("Player renamed:", data.playerName);
      if (data.playerId === playerId) {
        setPlayerName(data.playerName);
        storePlayerInfo(playerId, data.playerName);
      }
      // Request updated state to reflect name change
      if (playerId && roomId) {
        send({ type: "getState", roomId, playerId });
      }
    } else if (data.type === "voteTie") {
      // Handle vote tie message - store for display
      console.log("Received voteTie message:", data);
      setVoteTieMessage({
        displayMessage: data.displayMessage || data.message,
        message: data.message,
        phase: data.phase,
        requiresRevote: data.requiresRevote
      });
      // Clear message after 30 seconds or when phase changes (handled in gameState update)
      setTimeout(() => {
        setVoteTieMessage(null);
      }, 30000);
    } else if (data.type === "werewolfChat") {
      // Handle werewolf chat message from server
      console.log("Received werewolfChat message:", data);
      const serverMessage = {
        senderName: data.playerName,
        text: data.message,
        timestamp: data.timestamp || Date.now(),
        isOptimistic: false
      };
      
      setWerewolfChatMessages((prev) => {
        // Check if this message is from the current player (optimistic update)
        // If so, replace the optimistic message with the server-confirmed one
        const isFromCurrentPlayer = data.playerId === playerId;
        const isOptimisticDuplicate = isFromCurrentPlayer && prev.some(msg => 
          msg.isOptimistic && 
          msg.senderName === data.playerName && 
          msg.text === data.message &&
          Math.abs(msg.timestamp - (data.timestamp || Date.now())) < 5000 // Within 5 seconds
        );
        
        if (isOptimisticDuplicate) {
          // Replace optimistic message with server-confirmed message
          return prev.map(msg => 
            msg.isOptimistic && 
            msg.senderName === data.playerName && 
            msg.text === data.message &&
            Math.abs(msg.timestamp - (data.timestamp || Date.now())) < 5000
              ? serverMessage
              : msg
          );
        } else {
          // Check if message already exists (avoid duplicates)
          const alreadyExists = prev.some(msg => 
            msg.senderName === data.playerName && 
            msg.text === data.message &&
            Math.abs((msg.timestamp || 0) - (data.timestamp || Date.now())) < 2000 // Within 2 seconds
          );
          
          if (alreadyExists) {
            return prev; // Don't add duplicate
          }
          
          // Add new message from other players
          return [...prev, serverMessage];
        }
      });
    } else if (data.type === "error") {
      console.error("Server error:", data.message);
      alert("Lỗi: " + data.message);
    }
  };

  // Initialize WebSocket URL - will be updated when IP is detected
  const wsUrl = process.env.REACT_APP_WS_URL || (window.location.protocol === "https:" ? "wss://" : "ws://") + window.location.host;
  const { send, connected: wsConnected } = useWebSocket(wsUrl, handleMessage);

  // Show connection error only after 3 seconds of trying to connect
  useEffect(() => {
    if (!wsConnected) {
      const timer = setTimeout(() => {
        setShowConnectionError(true);
      }, 3000);
      return () => clearTimeout(timer);
    } else {
      setShowConnectionError(false);
    }
  }, [wsConnected]);

  // Auto-join if we have stored player info and WebSocket is connected (skip for first-time users)
  useEffect(() => {
    if (wsConnected && playerId && playerName && roomId && !isFirstTime) {
      // Auto-join when connection is restored or on initial connection
      // Check if we need to rejoin (no gameState or gameState doesn't match our playerId)
      const needsRejoin =
        !gameState ||
        !gameState.players?.some((p) => p.id === playerId) ||
        gameState.myId !== playerId;

      if (needsRejoin) {
        console.log(
          "[GameRoom] Auto-joining with stored player info:",
          playerId,
          playerName
        );
        send({
          type: "join",
          roomId,
          playerId: playerId,
          playerName: playerName.trim(),
        });
      }
    }
  }, [wsConnected, playerId, playerName, roomId, send, isFirstTime, gameState]);

  // Request state update periodically for waiting rooms to catch new players
  useEffect(() => {
    if (
      wsConnected &&
      playerId &&
      roomId &&
      gameState &&
      gameState.gameState === "waiting"
    ) {
      const interval = setInterval(() => {
        send({ type: "getState", roomId, playerId });
      }, 2000); // Check every 2 seconds for waiting rooms
      return () => clearInterval(interval);
    }
  }, [wsConnected, playerId, roomId, send, gameState]);

  // Request state update periodically to ensure sync
  // Continue polling even when game is finished so we can detect when a new game starts
  useEffect(() => {
    if (wsConnected && playerId && roomId) {
      // Determine poll interval based on game state
      // Poll less frequently for finished games, normal interval for active games
      let pollInterval = 3000; // Default: 3 seconds for active games
      let shouldPoll = true;
      
      if (gameState) {
        if (gameState.gameState === "finished") {
          // Poll less frequently when game is finished, so we can detect when a new game starts
          pollInterval = 5000; // 5 seconds for finished games
        } else if (gameState.gameState === "waiting") {
          // Don't poll while waiting - game hasn't started yet
          shouldPoll = false;
        }
        // Otherwise, use default 3 second interval for active games
      }
      // If gameState is null, poll with default interval to get initial state
      
      if (!shouldPoll) {
        return; // Don't start polling if game is waiting
      }
      
      console.log(`[GameRoom] Starting state polling with interval: ${pollInterval}ms, gameState: ${gameState?.gameState || 'null'}`);
      const interval = setInterval(() => {
        if (playerId && roomId) {
          console.log(`[GameRoom] Polling for state update (gameState: ${gameState?.gameState || 'null'})`);
          send({ type: "getState", roomId, playerId });
        }
      }, pollInterval);
      
      return () => {
        console.log(`[GameRoom] Stopping state polling`);
        clearInterval(interval);
      };
    }
  }, [wsConnected, playerId, roomId, send, gameState]);

  const handleJoin = () => {
    if (!playerName.trim()) {
      alert("Vui lòng nhập tên của bạn");
      return;
    }

    // Generate UUID if first time
    let currentPlayerId = playerId;
    if (isFirstTime || !currentPlayerId) {
      currentPlayerId = generateUUID();
      setPlayerId(currentPlayerId);
      storePlayerInfo(currentPlayerId, playerName.trim());
      setIsFirstTime(false);
    } else {
      // Update stored name if changed
      storePlayerInfo(currentPlayerId, playerName.trim());
    }

    send({
      type: "join",
      roomId,
      playerId: currentPlayerId,
      playerName: playerName.trim(),
    });
  };

  const handleRename = () => {
    if (!editingName.trim() || !playerId || !roomId) {
      return;
    }

    const newName = editingName.trim();
    // Update local storage
    storePlayerInfo(playerId, newName);
    setPlayerName(newName);

    // Send rename request to server
    send({
      type: "rename",
      roomId,
      playerId,
      playerName: newName,
    });

    setIsEditingName(false);
    setEditingName("");
  };

  const handleAction = (action, target = null, save = null) => {
    if (!playerId) {
      console.error("Cannot submit action: playerId is missing");
      return;
    }
    // Prevent dead players from taking actions (except hunter during day hunter phase - reactive kill when dead)
    const isDayHunterPhase = gameState?.phase === 'hunter' && gameState?.gameState === 'day' && gameState?.myRole === 'hunter' && action === 'hunter';
    // Night hunter phase: only alive hunters can aim (proactive) - server will enforce this
    // Day hunter phase: dead hunters can select target (reactive death phase)
    if (isDead && !isDayHunterPhase) {
      console.warn("Dead players cannot take actions (except hunter in day death phase)");
      return;
    }
    // Handle seer end turn separately
    if (action === "seer-end-turn") {
      send({
        type: "nextPhase",
        roomId,
        playerId,
      });
      return;
    }
    send({
      type: "nightAction",
      roomId,
      playerId,
      action,
      target,
      save,
    });
  };

  const handleSkipPhase = () => {
    if (!playerId) {
      console.error("Cannot skip phase: playerId is missing");
      return;
    }
    send({
      type: "nextPhase",
      roomId,
      playerId,
    });
  };

  const handleVote = (target) => {
    if (!playerId) {
      console.error("Cannot vote: playerId is missing");
      return;
    }
    // Prevent dead players from voting
    if (isDead) {
      console.warn("Dead players cannot vote");
      return;
    }

    if (!target) {
      console.error("Cannot vote: target is missing");
      alert("Vui lòng chọn người để bỏ phiếu");
      return;
    }

    // Check if we're in voting-decision phase - if so, send as 'vote' instead of 'target'
    const isDecisionPhase = gameState?.phase === 'voting-decision';
    
    if (isDecisionPhase) {
      // For voting-decision, target should be 'kill' or 'change-mind'
      if (target !== 'kill' && target !== 'change-mind') {
        console.error("Invalid vote for voting-decision phase:", target);
        alert("Lỗi: Phiếu bầu không hợp lệ");
        return;
      }
      console.log("Sending decision vote:", { type: "vote", roomId, playerId, vote: target });
      const success = send({
        type: "vote",
        roomId,
        playerId,
        vote: target, // Send as 'vote' for decision phase
      });
      if (!success) {
        console.error("Failed to send vote - WebSocket not connected");
        alert("Không thể gửi phiếu bầu. Vui lòng kiểm tra kết nối.");
      }
    } else {
      // For voting-accuse, send as 'target'
      console.log("Sending vote:", { type: "vote", roomId, playerId, target });
      const success = send({
        type: "vote",
        roomId,
        playerId,
        target,
      });
      if (!success) {
        console.error("Failed to send vote - WebSocket not connected");
        alert("Không thể gửi phiếu bầu. Vui lòng kiểm tra kết nối.");
      }
    }
  };

  const handleWerewolfChat = (message) => {
    if (!playerId || !roomId) {
      console.error("Cannot send chat: playerId or roomId is missing");
      return;
    }
    
    // Optimistic update: immediately add message to local state for the sender
    const currentPlayerName = gameState?.players.find(p => p.id === playerId)?.name || playerName;
    const optimisticMessage = {
      senderName: currentPlayerName,
      text: message,
      timestamp: Date.now(),
      isOptimistic: true // Mark as optimistic so we can replace it when server confirms
    };
    
    // Add message immediately for instant feedback
    setWerewolfChatMessages((prev) => [...prev, optimisticMessage]);
    
    // Send to server
    send({
      type: "werewolfChat",
      roomId,
      playerId,
      message,
    });
  };

  // Show join form if:
  // 1. First time user (no stored info)
  // 2. Or not connected yet
  // 3. Or not in the game yet (gameState doesn't have this player)
  // Check if player has joined the game
  const hasJoined =
    gameState && gameState.players?.some((p) => p.id === playerId);

  // If we have playerId and name but not joined yet, try to join automatically
  useEffect(() => {
    if (wsConnected && playerId && playerName && !hasJoined && !isFirstTime) {
      console.log("Player has ID but not in game, attempting to rejoin...");
      send({
        type: "join",
        roomId,
        playerId: playerId,
        playerName: playerName.trim(),
      });
    }
  }, [wsConnected, playerId, playerName, hasJoined, isFirstTime, roomId, send]);

  // Show join form if not joined yet
  if (!hasJoined) {
    return (
      <div className="game-room-container">
        <div className="container">
          <h1>Tham Gia Phòng Chơi</h1>
          <div className="room-info">
            <p>
              Mã Phòng: <span className="room-id">{roomId}</span>
            </p>
          </div>
          <div className="join-form">
            <label className="label">Tên Của Bạn:</label>
            <input
              type="text"
              className="input"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleJoin()}
              placeholder="Nhập tên của bạn"
              autoFocus
            />
            {isFirstTime && (
              <p
                style={{
                  fontSize: "14px",
                  color: "#666",
                  marginBottom: "10px",
                }}
              >
                Lần đầu tiên tham gia? Tên và ID của bạn sẽ được lưu để sử dụng
                lại.
              </p>
            )}
            {showConnectionError && !wsConnected && (
              <div
                style={{
                  padding: "10px",
                  background: "#fff3cd",
                  borderRadius: "6px",
                  marginBottom: "10px",
                }}
              >
                <p style={{ color: "#856404", margin: 0 }}>
                  ⚠️ Không thể kết nối đến server. Vui lòng đảm bảo server đang
                  chạy trên port 3001.
                </p>
              </div>
            )}
            <button
              className="button"
              onClick={handleJoin}
              disabled={!wsConnected || !playerName.trim()}
              style={{ width: "100%" }}
            >
              {wsConnected ? "Tham Gia" : "Đang kết nối..."}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!gameState) {
    return (
      <div className="game-room-container">
        <div className="container">
          <p>Đang tải...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="game-room-container">
      {/* Death overlay - don't show during hunter phase for hunter */}
      {isDead &&
        gameState &&
        gameState.gameState !== "finished" &&
        !deathOverlayDismissed &&
        !(gameState.phase === 'hunter' && gameState.myRole === 'hunter') && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.9)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              color: "#fff",
              fontSize: "24px",
              fontWeight: "bold",
            }}
          >
            <div
              style={{
                textAlign: "center",
                padding: "40px",
                backgroundColor: "#8b0000",
                borderRadius: "20px",
                border: "4px solid #ff0000",
                boxShadow: "0 0 30px rgba(255, 0, 0, 0.5)",
                maxWidth: "500px",
              }}
            >
              <div style={{ fontSize: "72px", marginBottom: "20px" }}>💀</div>
              <h1 style={{ margin: "20px 0", fontSize: "36px", color: "#fff" }}>
                Bạn đã bị giết
              </h1>
              <p style={{ margin: "10px 0", fontSize: "18px", opacity: 0.9 }}>
                Bạn có thể tiếp tục quan sát trò chơi nhưng không thể tham gia
                nữa.
              </p>
              <button
                className="button"
                onClick={() => setDeathOverlayDismissed(true)}
                style={{
                  marginTop: "30px",
                  padding: "12px 30px",
                  fontSize: "18px",
                  backgroundColor: "#fff",
                  color: "#8b0000",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: "bold",
                  transition: "all 0.3s",
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = "#f0f0f0";
                  e.target.style.transform = "scale(1.05)";
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = "#fff";
                  e.target.style.transform = "scale(1)";
                }}
              >
                Tiếp tục quan sát
              </button>
            </div>
          </div>
        )}

      {/* Dead player banner (shown when overlay is dismissed) - don't show during hunter phase for hunter */}
      {isDead &&
        gameState &&
        gameState.gameState !== "finished" &&
        deathOverlayDismissed &&
        !(gameState.phase === 'hunter' && gameState.myRole === 'hunter') && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              backgroundColor: "#8b0000",
              color: "#fff",
              padding: "15px",
              textAlign: "center",
              zIndex: 9998,
              borderBottom: "3px solid #ff0000",
              boxShadow: "0 2px 10px rgba(0, 0, 0, 0.3)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
              }}
            >
              <span style={{ fontSize: "24px" }}>💀</span>
              <span style={{ fontSize: "18px", fontWeight: "bold" }}>
                Bạn đã bị giết - Bạn đang quan sát với tư cách người chết
              </span>
              <span style={{ fontSize: "24px" }}>💀</span>
            </div>
          </div>
        )}

      <div
        className="container"
        style={{ marginTop: isDead && deathOverlayDismissed ? "60px" : "0" }}
      >
        {/* Player Name Display at Top */}
        {currentPlayerName && gameState && gameState.myId && (
          <div
            style={{
              marginBottom: "20px",
              padding: "12px 16px",
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
              border: "1px solid #e0e0e0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "10px",
            }}
          >
            {isEditingName ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  flex: 1,
                }}
              >
                <input
                  type="text"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && editingName.trim()) {
                      handleRename();
                    } else if (e.key === "Escape") {
                      setIsEditingName(false);
                      setEditingName("");
                    }
                  }}
                  autoFocus
                  style={{
                    flex: 1,
                    padding: "8px 12px",
                    border: "2px solid #667eea",
                    borderRadius: "6px",
                    fontSize: "16px",
                    outline: "none",
                  }}
                />
                <button
                  onClick={handleRename}
                  disabled={!editingName.trim()}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: "#667eea",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    cursor: editingName.trim() ? "pointer" : "not-allowed",
                    fontSize: "14px",
                    opacity: editingName.trim() ? 1 : 0.5,
                  }}
                >
                  Lưu
                </button>
                <button
                  onClick={() => {
                    setIsEditingName(false);
                    setEditingName("");
                  }}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: "#6c757d",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "14px",
                  }}
                >
                  Hủy
                </button>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  flex: 1,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <span style={{ fontSize: "18px", fontWeight: "bold" }}>
                    Tên: 
                  </span>
                  <span
                    style={{
                      fontSize: "18px",
                      fontWeight: "bold",
                      color: "#667eea",
                    }}
                  >
                    {currentPlayerName}
                  </span>
                </div>
                <button
                  onClick={() => {
                    setEditingName(currentPlayerName);
                    setIsEditingName(true);
                  }}
                  style={{
                    padding: "6px 10px",
                    backgroundColor: "transparent",
                    border: "1px solid #667eea",
                    borderRadius: "6px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#667eea",
                    transition: "all 0.2s",
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = "#f0f4ff";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = "transparent";
                  }}
                  title="Đổi tên"
                >
                  <PencilIcon size={16} color="#667eea" />
                </button>
              </div>
            )}
            <button
              onClick={() => {
                console.log("Manual refresh requested");
                if (playerId && roomId) {
                  send({ type: "getState", roomId, playerId });
                } else if (roomId) {
                  send({ type: "getState", roomId });
                }
              }}
              style={{
                padding: "8px 16px",
                backgroundColor: "#667eea",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "500",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                transition: "all 0.2s",
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = "#5568d3";
                e.target.style.transform = "scale(1.05)";
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = "#667eea";
                e.target.style.transform = "scale(1)";
              }}
              title="Làm mới trạng thái trò chơi"
            >
              <span>🔄</span>
              <span>Làm Mới</span>
            </button>
          </div>
        )}

        <div
          className="room-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            flexWrap: "wrap",
            gap: "15px",
          }}
        >
          <div style={{ flex: 1 }}>
            {gameState && gameState.myRole && (
              <div
                className="my-role"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  flexWrap: "wrap",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <div>
                    <strong>Vai trò của bạn: </strong>
                    {showRoles ? (
                      <span className={`role-badge role-${gameState.myRole}`}>
                        {ROLE_NAMES[gameState.myRole]}
                      </span>
                    ) : (
                      <span 
                        className="role-badge" 
                        style={{
                          backgroundColor: "#95a5a6",
                          color: "white",
                          opacity: 0.6,
                          display: "inline-flex",
                          alignItems: "center",
                          verticalAlign: "middle",
                        }}
                      >
                        ***
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => setShowRoles(!showRoles)}
                    style={{
                      padding: "6px 10px",
                      backgroundColor: "transparent",
                      border: "1px solid #667eea",
                      borderRadius: "6px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#667eea",
                      transition: "all 0.2s",
                      minWidth: "36px",
                      height: "32px",
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = "#f0f4ff";
                      e.target.style.borderColor = "#5568d3";
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = "transparent";
                      e.target.style.borderColor = "#667eea";
                    }}
                    title={showRoles ? "Ẩn vai trò" : "Hiện vai trò"}
                    aria-label={showRoles ? "Ẩn vai trò" : "Hiện vai trò"}
                  >
                    <EyeIcon size={18} color="#667eea" open={showRoles} />
                  </button>
                </div>
                {isDead && (
                  <span
                    style={{
                      backgroundColor: "#8b0000",
                      color: "#fff",
                      padding: "4px 12px",
                      borderRadius: "12px",
                      fontSize: "12px",
                      fontWeight: "bold",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    💀 Đã chết
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {gameState.gameState === "waiting" && (
          <div className="waiting-screen">
            <h2>Đang chờ người chơi...</h2>
            <PlayerList 
              players={gameState.players} 
              showRole={false}
            />
            <p style={{ marginTop: "20px", color: "#666" }}>
              Số người chơi: {gameState.players.length} /{" "}
              {gameState.config?.numPlayers || "?"}
            </p>
          </div>
        )}

        {gameState.gameState !== "waiting" && (
          <>
              <GamePhase
                gameState={gameState}
                onAction={handleAction}
                onVote={handleVote}
                onSkipPhase={handleSkipPhase}
                voteTieMessage={voteTieMessage}
                werewolfChatMessages={werewolfChatMessages}
                onWerewolfChat={handleWerewolfChat}
              />
            <PlayerList
              players={gameState.players}
              showRole={gameState.gameState === "finished"}
              myId={gameState.myId}
              votes={gameState.votes}
              decisionVotes={gameState.decisionVotes}
              phase={gameState.phase}
              accusedPlayer={gameState.accusedPlayer}
            />
          </>
        )}

        {gameState.gameState === "finished" && (
          <div className="game-finished">
            <h2>
              {gameState.winner === "werewolves"
                ? "🐺 Ma Sói Thắng! 🐺"
                : gameState.winner === "villagers"
                ? "🏆 Dân Làng Thắng! 🏆"
                : "Trò chơi kết thúc"}
            </h2>
            {gameState.winner && (
              <p style={{ marginTop: "10px", fontSize: "16px", color: "#666" }}>
                {gameState.winner === "werewolves"
                  ? "Ma Sói đã tiêu diệt tất cả Dân Làng!"
                  : "Dân Làng đã tiêu diệt tất cả Ma Sói!"}
              </p>
            )}
            <button
              className="button"
              onClick={() => navigate("/")}
              style={{ marginTop: "20px" }}
            >
              Về Trang Chủ
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default GameRoom;
